import { Button, Dialog, DialogContent, Typography } from '@mui/material';
import React from 'react';

import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { FeaturesExportForm } from 'components/ResultsExport/FeaturesExportForm';
import { useResultsExportContext } from 'components/ResultsExport/useResultsExportContext';
import { toWorkflowResultsExportContext } from 'components/ResultsExport/workflowResultsExportContext';
import { Study } from 'interfaces/study';
import { useResultsExportUrlState } from 'services/export/useResultsExportUrlState';
import { useStudyExportController } from 'services/export/useStudyExportController';
import { useWorkflowResultsSource } from './useWorkflowResultsSource';
import { WorkflowResultsSourceSelect } from './WorkflowResultsSourceSelect';

interface WorkflowFeaturesExportDialogProps {
  study?: Study;
  onClose: () => void;
}

// Opened from Review and publication. Features for the whole study, download only, never the Dataset chooser.
// Users who may view unpublished results choose the results source here; everyone else exports published results.
export const WorkflowFeaturesExportDialog: React.FC<WorkflowFeaturesExportDialogProps> = ({ study, onClose }) => {
  const baseContext = useResultsExportContext(study);
  const source = useWorkflowResultsSource('features', study);
  const { modeParams } = source;
  const { resultsMode, orchestrationIds } = modeParams;
  const runKey = orchestrationIds.join(',');
  // With several runs the context carries the first; the form exports every run in `orchestrationIds`.
  const context = React.useMemo(
    () => toWorkflowResultsExportContext(baseContext, { resultsMode, orchestrationId: orchestrationIds[0] }),
    [baseContext, resultsMode, runKey]
  );
  const { startExport, exportProgressDialog, isExportRunning } = useStudyExportController();
  const { clearDraft } = useResultsExportUrlState();

  const close = () => {
    clearDraft('features');
    source.clear();
    onClose();
  };

  const { availability, hasNoResults } = source;
  // Without the unpublished-results permission there is no source to choose, a study that does not allow
  // export has nothing to choose either, and neither has one without any results, so an unavailable export ends here.
  const isUnavailableForUser =
    availability.disabled && (!source.canViewUnpublishedResults || !study?.allowExport || hasNoResults);

  return (
    <>
      <Dialog
        open
        fullWidth
        maxWidth="md"
        onClose={close}
        onClick={(event) => event.stopPropagation()}
        aria-labelledby="workflow-features-export-title"
      >
        <DialogChromeHeader
          titleId="workflow-features-export-title"
          title="Export features"
          onClose={close}
          closeButtonAriaLabel="Close export"
        />
        {isUnavailableForUser || !study ? (
          <>
            <DialogContent dividers>
              <Typography variant="body2">{availability.note}</Typography>
            </DialogContent>
            <DialogChromeFooter>
              <Button color="inherit" onClick={close}>
                Close
              </Button>
            </DialogChromeFooter>
          </>
        ) : (
          <FeaturesExportForm
            context={context}
            startExport={startExport}
            isExportRunning={isExportRunning}
            onBack={close}
            backLabel="Close"
            downloadOnly
            resultSourceOwner="Workflow"
            blockedReason={availability.note}
            orchestrationIds={orchestrationIds}
            resultSourceControl={
              source.canViewUnpublishedResults ? (
                <WorkflowResultsSourceSelect
                  kind="features"
                  study={study}
                  selection={source.selection}
                  onChange={source.select}
                  runs={source.runs}
                  staleRunIds={source.staleRunIds}
                  isLoadingRuns={source.isLoadingRunOptions}
                  runNames={source.runNames}
                  hasRunsError={source.hasRunsError}
                  runsError={source.runsError}
                />
              ) : undefined
            }
          />
        )}
      </Dialog>
      {exportProgressDialog}
    </>
  );
};
